/**
 * Auto-layout engine using Dagre
 * Automatically positions nodes when positions are not provided
 */

import dagre from '@dagrejs/dagre'
import { Node, Edge } from '@xyflow/react'
import type { LayoutConfig, LayoutDirection } from '../types/schema'

const DEFAULT_NODE_WIDTH = 150
const DEFAULT_NODE_HEIGHT = 80
const DEFAULT_SPACING = 50
const DEFAULT_RANK_SPACING = 100

/**
 * Apply auto-layout to nodes using Dagre
 */
export function applyAutoLayout(
  nodes: Node[],
  edges: Edge[],
  layoutConfig?: LayoutConfig
): Node[] {
  // If all nodes have positions, skip auto-layout
  const needsLayout = nodes.some(
    (node) => !node.position || (node.position.x === 0 && node.position.y === 0)
  )

  if (!needsLayout && !layoutConfig?.algorithm) {
    return nodes
  }

  const algorithm = layoutConfig?.algorithm || 'dagre'

  switch (algorithm) {
    case 'dagre':
      return applyDagreLayout(nodes, edges, layoutConfig)
    case 'manual':
      return nodes
    // TODO: Add 'elk' and 'force' algorithms in future
    default:
      return applyDagreLayout(nodes, edges, layoutConfig)
  }
}

/**
 * Apply Dagre layout algorithm
 */
function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  layoutConfig?: LayoutConfig
): Node[] {
  const direction = layoutConfig?.direction || 'TB'
  const nodeSpacing = layoutConfig?.spacing?.node || DEFAULT_SPACING
  const rankSpacing = layoutConfig?.spacing?.rank || DEFAULT_RANK_SPACING

  // Create a new directed graph
  const graph = new dagre.graphlib.Graph()

  // Set graph properties
  graph.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    edgesep: layoutConfig?.spacing?.edge || 10,
  })

  // Default edge properties
  graph.setDefaultEdgeLabel(() => ({}))

  // Separate containers from regular nodes
  const containerNodes = nodes.filter((node) => node.type === 'container')
  const regularNodes = nodes.filter((node) => node.type !== 'container')

  // Add regular nodes to graph
  regularNodes.forEach((node) => {
    // Calculate size based on node content (structure-aware)
    const { width, height } = (() => {
      // Check for database table content
      if (node.data?.tableContent && typeof node.data.tableContent === 'object' && 'columns' in node.data.tableContent) {
        const columnCount = (node.data.tableContent as any).columns?.length || 5
        return {
          width: 350,  // Wide enough for column names and constraint badges
          height: 150 + (columnCount * 30)  // Base height + rows
        }
      }

      // Check for API resource content
      if (node.data?.resourceContent && typeof node.data.resourceContent === 'object' && 'methods' in node.data.resourceContent) {
        const methodCount = (node.data.resourceContent as any).methods?.length || 4
        return {
          width: 300,  // Adequate for method signatures
          height: 200 + (methodCount * 25)  // Base + methods
        }
      }

      // Check for API endpoint content
      if (node.data?.endpointContent && typeof node.data.endpointContent === 'object' && 'endpoints' in node.data.endpointContent) {
        const endpointCount = (node.data.endpointContent as any).endpoints?.reduce(
          (sum: number, group: any) => sum + (group.items?.length || 0), 0
        ) || 5
        return {
          width: 400,  // Wide for long endpoint paths
          height: 150 + (endpointCount * 35)  // Base + endpoint items
        }
      }

      // Default for regular icon/shape nodes
      return {
        width: (node.style?.width as number) || node.width || DEFAULT_NODE_WIDTH,
        height: (node.style?.height as number) || node.height || DEFAULT_NODE_HEIGHT
      }
    })()

    graph.setNode(node.id, {
      width,
      height,
    })
  })

  // Add edges to graph
  edges.forEach((edge) => {
    graph.setEdge(edge.source, edge.target)
  })

  // Run layout algorithm
  dagre.layout(graph)

  // Apply calculated positions back to nodes
  const layoutedNodes = regularNodes.map((node) => {
    const nodeWithPosition = graph.node(node.id)

    return {
      ...node,
      position: {
        // Dagre gives center position, we need top-left
        x: nodeWithPosition.x - nodeWithPosition.width / 2,
        y: nodeWithPosition.y - nodeWithPosition.height / 2,
      },
    }
  })

  // For containers, calculate bounding box of children
  const layoutedContainers = containerNodes.map((container) => {
    const childNodes = layoutedNodes.filter(
      (node) => node.data.parentContainer === container.id
    )

    if (childNodes.length === 0) {
      return container
    }

    const bounds = calculateBoundingBox(childNodes)
    const padding = 40

    return {
      ...container,
      position: {
        x: bounds.minX - padding,
        y: bounds.minY - padding,
      },
      style: {
        ...container.style,
        width: bounds.width + padding * 2,
        height: bounds.height + padding * 2,
      },
    }
  })

  return [...layoutedContainers, ...layoutedNodes]
}

/**
 * Calculate bounding box of nodes
 */
function calculateBoundingBox(nodes: Node[]): {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
} {
  if (nodes.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  nodes.forEach((node) => {
    const width = (node.style?.width as number) || node.width || DEFAULT_NODE_WIDTH
    const height = (node.style?.height as number) || node.height || DEFAULT_NODE_HEIGHT

    minX = Math.min(minX, node.position.x)
    minY = Math.min(minY, node.position.y)
    maxX = Math.max(maxX, node.position.x + width)
    maxY = Math.max(maxY, node.position.y + height)
  })

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

/**
 * Check if nodes need layout
 */
export function needsLayout(nodes: Node[]): boolean {
  // Check if any node has structured content (database tables, API resources, API endpoints)
  // These nodes need re-layout because MCP/parsers might have wrong sizes
  const hasStructuredContent = nodes.some((node) => {
    const data = node.data as any;
    return data?.tableContent || data?.resourceContent || data?.endpointContent;
  });

  // Also check for missing positions or (0,0) positions
  const hasMissingPositions = nodes.some(
    (node) =>
      !node.position ||
      (node.position.x === 0 && node.position.y === 0 && nodes.length > 1)
  );

  return hasStructuredContent || hasMissingPositions;
}

/**
 * Get recommended layout direction based on diagram type
 */
export function getRecommendedDirection(diagramType: string): LayoutDirection {
  switch (diagramType) {
    case 'architecture':
      return 'LR' // Left to right for architecture
    case 'flowchart':
      return 'TB' // Top to bottom for flowcharts
    case 'userflow':
      return 'TB' // Top to bottom for user flows
    case 'bpmn':
      return 'LR' // Left to right for process flows
    case 'erd':
      return 'TB' // Top to bottom for ERD
    case 'mindmap':
      return 'TB' // Top to bottom for mind maps
    case 'orgchart':
      return 'TB' // Top to bottom for org charts
    default:
      return 'TB'
  }
}
